import {
  Avatar,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  DropdownTitle,
  IconButton,
  Switch,
} from '@ads/components-react';
import {
  AcademicCapIcon,
  ArrowRightStartOnRectangleIcon,
  BanknotesIcon,
  Bars3Icon,
  BellIcon,
  BuildingOffice2Icon,
  CalculatorIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  DocumentCheckIcon,
  DocumentMagnifyingGlassIcon,
  EnvelopeIcon,
  FilmIcon,
  FolderIcon,
  GlobeAltIcon,
  HomeIcon,
  MapIcon,
  MapPinIcon,
  MegaphoneIcon,
  MoonIcon,
  PhotoIcon,
  PresentationChartBarIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  SunIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { Link, useRouterState } from '@tanstack/react-router';
import type { ParseKeys } from 'i18next';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import logoMbx from '@/assets/svg/logo-mbx.svg';
import logoMbxDark from '@/assets/svg/logo-mbxDark.svg';
import { useGetApiV1AccessIdPhoto } from '@/shared/api/generated/hooks/personRegisterController/useGetApiV1AccessIdPhoto';
import { usePermissions } from '@/shared/store/hooks/usePermissions';
import { useSession } from '@/shared/store/hooks/useSession';
import { useTheme } from '@/shared/store/hooks/useTheme';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
} from '@/shared/ui/atoms';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  if (parts.length <= 1) return first.charAt(0).toUpperCase();
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

// ─── Constantes ───────────────────────────────────────────────────────────────

interface NavLeaf {
  readonly kind: 'leaf';
  readonly to: string;
  readonly labelKey: ParseKeys<'common'>;
  readonly icon: ReactNode;
}

interface NavGroup {
  readonly kind: 'group';
  readonly id: string;
  readonly labelKey: ParseKeys<'common'>;
  readonly icon: ReactNode;
  readonly children: readonly NavLeaf[];
}

type NavItem = NavLeaf | NavGroup;

const NAV_ITEMS: readonly NavItem[] = [
  {
    kind: 'leaf',
    to: '/',
    labelKey: 'appShell.nav.home',
    icon: <HomeIcon className="h-xxxs w-xxxs" />,
  },
  {
    kind: 'leaf',
    to: '/access-management',
    labelKey: 'appShell.nav.accessManagement',
    icon: <ShieldCheckIcon className="h-xxxs w-xxxs" />,
  },
  {
    kind: 'leaf',
    to: '/class-modules',
    labelKey: 'appShell.nav.classModules',
    icon: <AcademicCapIcon className="h-xxxs w-xxxs" />,
  },
  {
    kind: 'leaf',
    to: '/class-planning',
    labelKey: 'appShell.nav.classPlanning',
    icon: <CalendarDaysIcon className="h-xxxs w-xxxs" />,
  },
  {
    kind: 'group',
    id: 'exams',
    labelKey: 'appShell.nav.exams',
    icon: <ClipboardDocumentCheckIcon className="h-xxxs w-xxxs" />,
    children: [
      {
        kind: 'leaf',
        to: '/exams',
        labelKey: 'appShell.nav.examManagement',
        icon: <DocumentCheckIcon className="h-xxxs w-xxxs" />,
      },
      {
        kind: 'leaf',
        to: '/exam/correction-dashboard',
        labelKey: 'appShell.nav.correctionDashboard',
        icon: <ClipboardDocumentCheckIcon className="h-xxxs w-xxxs" />,
      },
      {
        kind: 'leaf',
        to: '/exam/grade-config',
        labelKey: 'appShell.nav.gradeConfig',
        icon: <CalculatorIcon className="h-xxxs w-xxxs" />,
      },
      {
        kind: 'leaf',
        to: '/exam/revision',
        labelKey: 'appShell.nav.examRevision',
        icon: <DocumentMagnifyingGlassIcon className="h-xxxs w-xxxs" />,
      },
      {
        kind: 'leaf',
        to: '/question-library',
        labelKey: 'appShell.nav.questionLibrary',
        icon: <ClipboardDocumentListIcon className="h-xxxs w-xxxs" />,
      },
    ],
  },
  {
    kind: 'leaf',
    to: '/persons',
    labelKey: 'appShell.nav.personManagement',
    icon: <UsersIcon className="h-xxxs w-xxxs" />,
  },
  {
    kind: 'leaf',
    to: '/enrollments',
    labelKey: 'appShell.nav.enrollments',
    icon: <AcademicCapIcon className="h-xxxs w-xxxs" />,
  },
  {
    kind: 'leaf',
    to: '/individual-payment',
    labelKey: 'appShell.nav.individualPayment',
    icon: <BanknotesIcon className="h-xxxs w-xxxs" />,
  },
  {
    kind: 'group',
    id: 'tutorAI',
    labelKey: 'appShell.nav.tutorAI',
    icon: <SparklesIcon className="h-xxxs w-xxxs" />,
    children: [
      {
        kind: 'leaf',
        to: '/tutor-ai/usage',
        labelKey: 'appShell.nav.tutorAIUsage',
        icon: <ChartBarIcon className="h-xxxs w-xxxs" />,
      },
      {
        kind: 'leaf',
        to: '/tutor-ai/config',
        labelKey: 'appShell.nav.tutorAIConfig',
        icon: <Cog6ToothIcon className="h-xxxs w-xxxs" />,
      },
      {
        kind: 'leaf',
        to: '/tutor-ai/classes',
        labelKey: 'appShell.nav.tutorAIClasses',
        icon: <UsersIcon className="h-xxxs w-xxxs" />,
      },
      {
        kind: 'leaf',
        to: '/tutor-ai/students',
        labelKey: 'appShell.nav.tutorAIStudents',
        icon: <AcademicCapIcon className="h-xxxs w-xxxs" />,
      },
    ],
  },
  {
    kind: 'group',
    id: 'location',
    labelKey: 'appShell.nav.location',
    icon: <MapPinIcon className="h-xxxs w-xxxs" />,
    children: [
      {
        kind: 'leaf',
        to: '/location/countries',
        labelKey: 'appShell.nav.countries',
        icon: <MapPinIcon className="h-xxxs w-xxxs" />,
      },
      {
        kind: 'leaf',
        to: '/location/states',
        labelKey: 'appShell.nav.states',
        icon: <MapIcon className="h-xxxs w-xxxs" />,
      },
      {
        kind: 'leaf',
        to: '/location/cities',
        labelKey: 'appShell.nav.cities',
        icon: <BuildingOffice2Icon className="h-xxxs w-xxxs" />,
      },
    ],
  },
  {
    kind: 'group',
    id: 'marketing',
    labelKey: 'appShell.nav.marketing',
    icon: <MegaphoneIcon className="h-xxxs w-xxxs" />,
    children: [
      {
        kind: 'leaf',
        to: '/banners',
        labelKey: 'appShell.nav.banner',
        icon: <PhotoIcon className="h-xxxs w-xxxs" />,
      },
      {
        kind: 'leaf',
        to: '/notification',
        labelKey: 'appShell.nav.notification',
        icon: <BellIcon className="h-xxxs w-xxxs" />,
      },
      {
        kind: 'leaf',
        to: '/slide-library',
        labelKey: 'appShell.nav.slideEditor',
        icon: <PresentationChartBarIcon className="h-xxxs w-xxxs" />,
      },
    ],
  },
  {
    kind: 'group',
    id: 'cadastro',
    labelKey: 'appShell.nav.cadastro',
    icon: <FolderIcon className="h-xxxs w-xxxs" />,
    children: [
      {
        kind: 'leaf',
        to: '/series',
        labelKey: 'appShell.nav.series',
        icon: <FilmIcon className="h-xxxs w-xxxs" />,
      },
    ],
  },
];

function isLeafActive(leaf: NavLeaf, pathname: string): boolean {
  return leaf.to === '/' ? pathname === '/' : pathname.startsWith(leaf.to);
}

function findActiveGroupId(items: readonly NavItem[], pathname: string): string | null {
  for (const item of items) {
    if (item.kind === 'group' && item.children.some(c => isLeafActive(c, pathname))) {
      return item.id;
    }
  }
  return null;
}

const SIDEBAR_EXPANDED = 312;
const SIDEBAR_COLLAPSED = 64;
const DESKTOP_BREAKPOINT = 1024;

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AppShellProps {
  readonly children: ReactNode;
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Organism AppShell — layout global com sidebar recolhível e header fixo.
 *
 * Em desktop (≥ 1024px): sidebar fixa recolhível (ícone + texto ou só ícones).
 * Em mobile (< 1024px): sidebar como drawer overlay com backdrop.
 * O header expõe slots de notificações, mensagens, ajuda e menu do usuário.
 *
 * @example
 * ```tsx
 * <AppShell>
 *   <Outlet />
 * </AppShell>
 * ```
 */
export function AppShell({ children }: AppShellProps) {
  const { t, i18n } = useTranslation('common');
  const { session, logout } = useSession();
  const { theme, toggle } = useTheme();
  const { isRouteAllowed } = usePermissions();

  const visibleNavItems = useMemo<NavItem[]>(
    () =>
      NAV_ITEMS.flatMap<NavItem>(item => {
        if (item.kind === 'leaf') {
          return isRouteAllowed(item.to) ? [item] : [];
        }
        const visibleChildren = item.children.filter(c => isRouteAllowed(c.to));
        return visibleChildren.length > 0 ? [{ ...item, children: visibleChildren }] : [];
      }),
    [isRouteAllowed],
  );
  const pathname = useRouterState({ select: s => s.location.pathname });

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= DESKTOP_BREAKPOINT);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<string[]>([]);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Fecha sidebar mobile ao navegar
  useEffect(() => {
    setIsMobileOpen(false);
  }, []);

  // Abre automaticamente o grupo cujo filho corresponde à rota atual.
  // Só dispara quando o grupo ativo muda — não impede o usuário de fechar manualmente.
  const activeGroupId = useMemo(
    () => findActiveGroupId(visibleNavItems, pathname),
    [pathname, visibleNavItems],
  );
  useEffect(() => {
    if (!activeGroupId) return;
    setOpenGroups(prev => (prev.includes(activeGroupId) ? prev : [...prev, activeGroupId]));
  }, [activeGroupId]);

  const { data: photoData } = useGetApiV1AccessIdPhoto(session?.personId ?? 0, {
    query: { enabled: !!session?.personId },
    client: { responseType: 'blob' },
  });

  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!photoData) {
      setPhotoUrl(null);
      return;
    }
    const url = URL.createObjectURL(photoData as Blob);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photoData]);

  const sidebarW = isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;
  const initials = session?.name ? getInitials(session.name) : 'U';
  const isSidebarVisible = isDesktop || isMobileOpen;
  const isIconOnly = isCollapsed && isDesktop;

  return (
    <div className="h-screen overflow-hidden bg-ctx-layout-body">
      {/* ── Overlay mobile ──────────────────────────────────────────────────── */}
      {!isDesktop && isMobileOpen && (
        <div
          className="fixed left-none top-none z-20 h-full w-full bg-base-gray-900 opacity-medium"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <aside
        style={{ width: `${isDesktop ? sidebarW : SIDEBAR_EXPANDED}px` }}
        className={[
          'fixed left-none top-none z-30 flex h-full flex-col overflow-hidden',
          'bg-ctx-layout-surface shadow-level2 transition-[width,transform] duration-200 ease-in-out',
          isSidebarVisible ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
        aria-label={t('appShell.nav.label')}
      >
        {/* Botão de toggle */}
        <div className="flex h-xl shrink-0 items-center border-b-hairline border-ctx-layout-border px-micro">
          <IconButton
            ariaLabel={isCollapsed ? t('appShell.sidebar.expand') : t('appShell.sidebar.collapse')}
            icon={Bars3Icon}
            hierarchy="tertiary"
            onClick={() => {
              if (isDesktop) {
                setIsCollapsed(c => !c);
              } else {
                setIsMobileOpen(false);
              }
            }}
          />
        </div>

        {/* Navegação */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-xs at-scrollbar">
          <Accordion type="multiple" value={openGroups} onValueChange={setOpenGroups} asChild>
            <ul className="flex flex-col gap-nano px-micro">
              {visibleNavItems.map(item => {
                if (item.kind === 'leaf') {
                  const isActive = isLeafActive(item, pathname);
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to as never}
                        search={{} as never}
                        title={isIconOnly ? t(item.labelKey) : undefined}
                        aria-current={isActive ? 'page' : undefined}
                        className={[
                          'flex items-center rounded-sm transition-colors duration-150 ts-paragraph-xs',
                          isIconOnly ? 'justify-center p-nano' : 'gap-nano p-nano',
                          isActive
                            ? 'bg-ctx-interactive-secondary text-ctx-content-title'
                            : 'text-ctx-content-base hover:bg-ctx-interactive-secondaryHover hover:text-ctx-content-base',
                        ].join(' ')}
                      >
                        <span
                          className={[
                            'shrink-0 transition-colors',
                            isActive ? 'text-ctx-content-brand' : 'text-ctx-content-placeholder',
                          ].join(' ')}
                        >
                          {item.icon}
                        </span>
                        {!isIconOnly && (
                          <span className="overflow-hidden whitespace-nowrap">
                            {t(item.labelKey)}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                }

                const hasActiveChild = item.children.some(c => isLeafActive(c, pathname));

                if (isIconOnly) {
                  // Sidebar recolhido: clicar no grupo expande a sidebar e abre o accordion.
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        title={t(item.labelKey)}
                        aria-label={t(item.labelKey)}
                        onClick={() => {
                          setIsCollapsed(false);
                          setOpenGroups(prev =>
                            prev.includes(item.id) ? prev : [...prev, item.id],
                          );
                        }}
                        className={[
                          'flex w-full items-center justify-center rounded-sm p-nano transition-colors duration-150',
                          hasActiveChild
                            ? 'bg-ctx-interactive-secondary text-ctx-content-title'
                            : 'text-ctx-content-base hover:bg-ctx-interactive-secondaryHover',
                        ].join(' ')}
                      >
                        <span
                          className={[
                            'shrink-0 transition-colors',
                            hasActiveChild
                              ? 'text-ctx-content-brand'
                              : 'text-ctx-content-placeholder',
                          ].join(' ')}
                        >
                          {item.icon}
                        </span>
                      </button>
                    </li>
                  );
                }

                return (
                  <AccordionItem key={item.id} value={item.id} asChild>
                    <li>
                      <AccordionTrigger
                        className={[
                          'gap-nano rounded-sm p-nano ts-paragraph-xs',
                          hasActiveChild
                            ? 'text-ctx-content-title'
                            : 'text-ctx-content-base hover:bg-ctx-interactive-secondaryHover',
                        ].join(' ')}
                      >
                        <span
                          className={[
                            'shrink-0 transition-colors',
                            hasActiveChild
                              ? 'text-ctx-content-brand'
                              : 'text-ctx-content-placeholder',
                          ].join(' ')}
                        >
                          {item.icon}
                        </span>
                        <span className="flex-1 overflow-hidden whitespace-nowrap text-ctx-content-base">
                          {t(item.labelKey)}
                        </span>
                        <ChevronDownIcon
                          className="h-xxxs w-xxxs shrink-0 text-ctx-content-placeholder transition-transform duration-200 group-data-[state=open]:rotate-180"
                          aria-hidden="true"
                        />
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="mt-nano flex flex-col gap-nano pl-xxs border-b-hairline border-ctx-layout-border pb-nano">
                          {item.children.map(child => {
                            const isActive = isLeafActive(child, pathname);
                            return (
                              <li key={child.to}>
                                <Link
                                  to={child.to as never}
                                  search={{} as never}
                                  aria-current={isActive ? 'page' : undefined}
                                  className={[
                                    'flex items-center gap-nano rounded-sm p-nano ts-paragraph-xs transition-colors duration-150',
                                    isActive
                                      ? 'bg-ctx-interactive-secondary text-ctx-content-title'
                                      : 'text-ctx-content-base hover:bg-ctx-interactive-secondaryHover',
                                  ].join(' ')}
                                >
                                  <span
                                    className={[
                                      'shrink-0 transition-colors',
                                      isActive
                                        ? 'text-ctx-content-brand'
                                        : 'text-ctx-content-placeholder',
                                    ].join(' ')}
                                  >
                                    {child.icon}
                                  </span>
                                  <span className="overflow-hidden whitespace-nowrap">
                                    {t(child.labelKey)}
                                  </span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </AccordionContent>
                    </li>
                  </AccordionItem>
                );
              })}
            </ul>
          </Accordion>
        </nav>
      </aside>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <header
        style={isDesktop ? { paddingLeft: `${sidebarW}px` } : undefined}
        className="fixed top-none z-10 flex h-xl w-full items-center justify-between border-b-hairline border-ctx-layout-border bg-ctx-layout-surface px-md shadow-level1 transition-[padding-left] duration-200 ease-in-out"
      >
        <div className="flex items-center justify-center">
          {/* Mobile: abre sidebar */}
          {!isDesktop && (
            <IconButton
              icon={Bars3Icon}
              hierarchy="tertiary"
              size="md"
              ariaLabel={t('appShell.sidebar.expand')}
              onClick={() => setIsMobileOpen(true)}
            />
          )}

          <Link to={'/'}>
            <img
              src={theme === 'dark' ? logoMbxDark : logoMbx}
              alt="MBX"
              className="h-xs w-auto pl-xs"
            />
          </Link>
        </div>

        {/* Ações */}
        <div className="flex items-center gap-xxs">
          {/* Mensagens */}
          <IconButton
            icon={EnvelopeIcon}
            hierarchy="tertiary"
            size="md"
            ariaLabel={t('appShell.header.messages')}
          />

          {/* Notificações — com badge */}
          <div className="relative">
            <IconButton
              icon={BellIcon}
              hierarchy="tertiary"
              size="md"
              ariaLabel={t('appShell.header.notifications')}
            />
            <span
              className="pointer-events-none absolute right-quark top-quark h-nano w-nano rounded-circular bg-base-danger-500"
              aria-hidden="true"
            />
          </div>

          {/* Ajuda */}
          <IconButton
            icon={QuestionMarkCircleIcon}
            hierarchy="tertiary"
            size="md"
            ariaLabel={t('appShell.header.help')}
          />

          {/* Menu do usuário */}
          <Dropdown
            trigger={
              <button type="button">
                <Avatar src={photoUrl ?? undefined} fallback={initials} size="md" />
              </button>
            }
            align="end"
          >
            {/* Informações do usuário */}
            <DropdownTitle hasDivider>
              <div className="flex items-center gap-mini">
                <Avatar src={photoUrl ?? undefined} fallback={initials} size="md" />
                <div className="flex flex-col">
                  <span className="text-ctx-content-title ts-paragraph-xs">{session?.name}</span>
                  <span className="text-ctx-content-placeholder ts-caption-xxs">
                    {session?.email}
                  </span>
                </div>
              </div>
            </DropdownTitle>

            {/* Tema */}
            <DropdownItem hasDivider onSelect={toggle}>
              <div className="flex w-full items-center justify-between gap-sm">
                <div className="flex items-center gap-nano">
                  {theme === 'dark' ? (
                    <MoonIcon className="h-xxxs w-xxxs text-ctx-content-placeholder" />
                  ) : (
                    <SunIcon className="h-xxxs w-xxxs text-ctx-content-placeholder" />
                  )}
                  <span className="text-ctx-content-base ts-paragraph-xs">
                    {t('appShell.header.theme')}
                  </span>
                </div>
                <Switch checked={theme === 'dark'} size="sm" />
              </div>
            </DropdownItem>

            {/* Idioma */}
            <Dropdown
              className="min-w-[20rem]"
              trigger={
                <DropdownItem hasDivider>
                  <DropdownItemContent
                    leadingIcon={GlobeAltIcon}
                    trailingIcon={() => (
                      <ChevronRightIcon
                        className={`h-xxxs w-xxxs transition-transform duration-200 ${isLangOpen ? 'rotate-90' : ''}`}
                      />
                    )}
                  >
                    {t('appShell.header.language')}
                  </DropdownItemContent>
                </DropdownItem>
              }
              onOpenChange={setIsLangOpen}
              align="center"
              alignOffset={16}
            >
              <DropdownItem
                onSelect={() => void i18n.changeLanguage('pt-BR')}
                status={i18n.language.startsWith('pt') ? 'active' : 'default'}
              >
                <DropdownItemContent>{t('appShell.header.languagePtBR')}</DropdownItemContent>
              </DropdownItem>
              <DropdownItem
                onSelect={() => void i18n.changeLanguage('en-US')}
                status={i18n.language.startsWith('en') ? 'active' : 'default'}
              >
                <DropdownItemContent>{t('appShell.header.languageEnUS')}</DropdownItemContent>
              </DropdownItem>
              <DropdownItem
                onSelect={() => void i18n.changeLanguage('es-ES')}
                status={i18n.language.startsWith('es') ? 'active' : 'default'}
              >
                <DropdownItemContent>{t('appShell.header.languageEsES')}</DropdownItemContent>
              </DropdownItem>
            </Dropdown>

            {/* Sair */}
            <DropdownItem onSelect={logout}>
              <DropdownItemContent leadingIcon={ArrowRightStartOnRectangleIcon}>
                {t('appShell.header.logout')}
              </DropdownItemContent>
            </DropdownItem>
          </Dropdown>
        </div>
      </header>

      {/* ── Conteúdo principal ──────────────────────────────────────────────── */}
      <main
        style={isDesktop ? { marginLeft: `${sidebarW}px` } : undefined}
        className="at-scrollbar mt-xs h-full overflow-y-auto p-md transition-[margin-left] duration-200 ease-in-out lg:p-lg"
      >
        {!pathname.startsWith('/notification') && (
          <Alert type="warning" className="mb-nano">
            {t('appShell.migrationAlert')}
          </Alert>
        )}
        {children}
      </main>
    </div>
  );
}
